import { supabase } from "@/integrations/supabase/client";

// Types for user analytics
export type TopEngagedUser = {
  id: string | number;
  name: string;
  email: string;
  views: number;
  reads: number;
  plays: number;
  total: number;
  lastReadAt?: string | null;
  subscriptionStatus?: string;
};

export type EngagementDetailsReturn = {
  activeUsers: {
    totalUnique: number;
    previousTotalUnique?: number;
    percentChange?: number;
    uniqueViewers: number;
    uniqueReaders: number;
    uniqueListeners: number;
  };
  uniqueActiveUsersSeries: Array<{ month: string; count: number }>;
  heatmap: number[][];
  topUsers: TopEngagedUser[];
  topUsersTotal: number;
};

// Global user metrics (not filtered by date range)
export const fetchGlobalUserMetrics = async () => {
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const nowIso = new Date().toISOString();

    const [
      { count: newUserCount },
      { data: userRoles },
      { count: activeSubscribersCount },
      { count: totalSubscribersCount }
    ] = await Promise.all([
      supabase.from("profiles").select("*", { count: "exact", head: true }).gte("registered_at", thirtyDaysAgo),
      supabase.from("user_roles").select("id, name"),
      supabase
        .from("user_subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "active")
        .lte("subscription_start", nowIso)
        .or(`subscription_end.is.null,subscription_end.gt.${nowIso}`),
      supabase.from("user_subscriptions").select("*", { count: "exact", head: true })
    ]);

    // Get user counts for each role using user_to_roles table
    const userRoleCounts = await Promise.all(
      (userRoles || []).map(async (role) => {
        let userCount = 0;
        
        if (role.id === 5) {
          // For role ID 5 ("User"), count only users who have ONLY this role
          // First, get all users who have role 5 (with pagination)
          const fetchAllUsersWithRole5 = async (): Promise<Array<{ user_id: string | number }>> => {
            const allRecords: Array<{ user_id: string | number }> = [];
            let from = 0;
            const pageSize = 1000;
            let hasMore = true;

            while (hasMore) {
              const { data, error } = await supabase
                .from("user_to_roles")
                .select("user_id")
                .eq("role_id", 5)
                .order("user_id", { ascending: true })
                .range(from, from + pageSize - 1);
              
              if (error) {
                console.error("Error fetching users with role 5:", error);
                throw error;
              }

              if (data && data.length > 0) {
                allRecords.push(...data);
                hasMore = data.length === pageSize;
                from += pageSize;
              } else {
                hasMore = false;
              }
            }

            return allRecords;
          };
          
          const usersWithRole5 = await fetchAllUsersWithRole5();
          
          if (usersWithRole5 && usersWithRole5.length > 0) {
            const userIdsWithRole5 = usersWithRole5.map(u => u.user_id);
            
            // Then, get users who have multiple roles (with pagination)
            // Note: Supabase .in() has a limit, so we need to chunk the userIds array if it's large
            const fetchAllUsersWithMultipleRoles = async (): Promise<Array<{ user_id: string | number }>> => {
              const allRecords: Array<{ user_id: string | number }> = [];
              const chunkSize = 1000; // Supabase .in() limit
              
              // Process userIds in chunks
              for (let i = 0; i < userIdsWithRole5.length; i += chunkSize) {
                const chunk = userIdsWithRole5.slice(i, i + chunkSize);
                const userIdsNumbers = chunk.filter((id): id is number => typeof id === "number");
                
                if (userIdsNumbers.length === 0) continue;
                
                let from = 0;
                const pageSize = 1000;
                let hasMore = true;

                while (hasMore) {
                  const { data, error } = await supabase
                    .from("user_to_roles")
                    .select("user_id")
                    .in("user_id", userIdsNumbers)
                    .neq("role_id", 5)
                    .order("user_id", { ascending: true })
                    .range(from, from + pageSize - 1);
                  
                  if (error) {
                    console.error("Error fetching users with multiple roles:", error);
                    throw error;
                  }

                  if (data && data.length > 0) {
                    allRecords.push(...data);
                    hasMore = data.length === pageSize;
                    from += pageSize;
                  } else {
                    hasMore = false;
                  }
                }
              }

              return allRecords;
            };
            
            const usersWithMultipleRoles = await fetchAllUsersWithMultipleRoles();
            
            // Count users who have role 5 but no other roles
            const usersWithOnlyRole5 = userIdsWithRole5.filter(
              userId => !usersWithMultipleRoles?.some(u => u.user_id === userId)
            );
            
            userCount = usersWithOnlyRole5.length;
          }
        } else {
          // For other roles, count all users with this role
          const { count } = await supabase
            .from("user_to_roles")
            .select("*", { count: "exact", head: true })
            .eq("role_id", role.id);
          userCount = count || 0;
        }
        
        return {
          ...role,
          userCount
        };
      })
    );

    // Calculate global registration insights for ALL users (not filtered by role)
    // Fetch all user registrations with pagination
    const fetchAllUserRegistrations = async (): Promise<{ registered_at: string }[]> => {
      const allRecords: { registered_at: string }[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("profiles")
          .select("registered_at")
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching user registrations:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };
    
    const allTimeUsers = await fetchAllUserRegistrations();

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const globalMonthlySignups = Array(12).fill(0);
    
    allTimeUsers?.forEach((user) => {
      const date = new Date(user.registered_at);
      const month = date.getMonth();
      globalMonthlySignups[month]++;
    });

    const globalMonthlyData = months.map((month, index) => ({
      month,
      count: globalMonthlySignups[index],
    }));

    const globalPeakMonth = globalMonthlyData.reduce(
      (peak, current) => current.count > peak.count ? current : peak,
      globalMonthlyData[0] || { month: "N/A", count: 0 }
    );

    const globalTotalRegistrations = globalMonthlyData.reduce((sum, month) => sum + month.count, 0);
    
    // Calculate average monthly signups starting from September (month index 8)
    const monthsFromSeptember = globalMonthlyData.slice(8); // Sep, Oct, Nov, Dec
    const totalFromSeptember = monthsFromSeptember.reduce((sum, month) => sum + month.count, 0);
    const globalAverageMonthly = Math.round(totalFromSeptember / Math.max(monthsFromSeptember.length, 1));

    // Age group distribution from profiles (with pagination)
    const fetchAllProfileAges = async (): Promise<Array<{ age_group: string | null }>> => {
      const allRecords: Array<{ age_group: string | null }> = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("profiles")
          .select("age_group")
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching profile ages:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    const profilesAges = await fetchAllProfileAges();
    const ageGroupCounts: Record<string, number> = {};
    profilesAges?.forEach((p) => {
      const key = (p as { age_group: string | null }).age_group || "Unknown";
      ageGroupCounts[key] = (ageGroupCounts[key] || 0) + 1;
    });

    return {
      newUsers: newUserCount || 0,
      userRoles: userRoleCounts || [],
      ageGroups: Object.entries(ageGroupCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      activeSubscribers: activeSubscribersCount || 0,
      totalSubscribers: totalSubscribersCount || 0,
      globalInsights: {
        peakMonth: globalPeakMonth.month,
        peakCount: globalPeakMonth.count,
        totalRegistrations: globalTotalRegistrations,
        averageMonthly: globalAverageMonthly,
      },
    };
  } catch (error) {
    console.error("Error fetching global user metrics:", error);
    throw error;
  }
};

// User analytics data (filtered by date range)
export const fetchUserAnalytics = async (dateRange?: { from: Date; to: Date }) => {
  try {
    // Ensure we have a valid date range
    if (!dateRange?.from || !dateRange?.to) {
      throw new Error("Date range is required");
    }
    
    const fromDate = dateRange.from;
    const toDate = dateRange.to;
    
    // Ensure fromDate starts at the beginning of the day (00:00:00) in local time
    const fromDateStart = new Date(fromDate);
    fromDateStart.setHours(0, 0, 0, 0);
    
    // Ensure toDate ends at the end of the day (23:59:59.999) in local time
    const toDateEnd = new Date(toDate);
    toDateEnd.setHours(23, 59, 59, 999);
    
    const fromISO = fromDateStart.toISOString();
    const toISO = toDateEnd.toISOString();

    // Helper function to fetch all records with pagination (Supabase has 1000 row limit)
    const fetchAllUsers = async (): Promise<{ registered_at: string }[]> => {
      const allRecords: { registered_at: string }[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("profiles")
          .select("registered_at")
          .gte("registered_at", fromISO)
          .lte("registered_at", toISO)
          .order("registered_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching users:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Helper function to fetch all subscriptions with pagination
    const fetchAllSubscriptions = async (): Promise<{ subscription_start: string; status: string }[]> => {
      const allRecords: { subscription_start: string; status: string }[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("user_subscriptions")
          .select("subscription_start, status")
          .eq("status", "active")
          .gte("subscription_start", fromISO)
          .lte("subscription_start", toISO)
          .order("subscription_start", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching subscriptions:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Fetch all users (with pagination to handle >1000 records)
    const [usersByMonth, recentUsers, activeSubsInRange] = await Promise.all([
      fetchAllUsers(),
      supabase
        .from("profiles")
        .select("id, name, email, avatar_url, registered_at")
        .order("registered_at", { ascending: false })
        .limit(5)
        .then(({ data }) => data || []),
      fetchAllSubscriptions(),
    ]);

    // Process monthly signups based on date range
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthlySignups = Array(12).fill(0);
    
    usersByMonth?.forEach((user) => {
      const date = new Date(user.registered_at);
      const month = date.getMonth();
      monthlySignups[month]++;
    });

    // Calculate the number of days in the date range using normalized dates
    const daysDiff = Math.ceil((toDateEnd.getTime() - fromDateStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // If date range spans multiple years, show monthly data for the selected range
    // Use normalized dates for comparisons to ensure consistency
    const isSameYear = fromDateStart.getFullYear() === toDateEnd.getFullYear();
    const isSameMonth = isSameYear && fromDateStart.getMonth() === toDateEnd.getMonth();
    const isSameDay = isSameMonth && fromDateStart.getDate() === toDateEnd.getDate();
    const shouldShowDaily = daysDiff <= 28; // Show daily data for ranges of 28 days or less
    
    let monthlySignupsData;
    // Counts of new subscribers that started their subscription in the period
    let monthlyNewSubscribersData;
    if (isSameDay) {
      // Same day - show hourly data (if we had it) or just the count
      monthlySignupsData = [{
        month: `${months[fromDateStart.getMonth()]} ${fromDateStart.getDate()}, ${fromDateStart.getFullYear()}`,
        count: usersByMonth?.length || 0,
      }];
      monthlyNewSubscribersData = [{
        month: `${months[fromDateStart.getMonth()]} ${fromDateStart.getDate()}, ${fromDateStart.getFullYear()}`,
        count: activeSubsInRange?.length || 0,
      }];
    } else if (isSameMonth) {
      // Same month - show daily data
      const daysInMonth = new Date(fromDateStart.getFullYear(), fromDateStart.getMonth() + 1, 0).getDate();
      const dailyData = [];
      const dailySubs = Array(daysInMonth).fill(0);
      
      // Helper function to get local date components from a UTC date string
      const getLocalDateComponents = (utcDateString: string) => {
        const date = new Date(utcDateString);
        return {
          year: date.getFullYear(),
          month: date.getMonth(),
          day: date.getDate(),
        };
      };
      
      // Get the actual day range from normalized dates
      const startDay = fromDateStart.getDate();
      const endDay = toDateEnd.getDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        // Count users by comparing local date components (day, month, year)
        // This ensures we match users to the correct local day regardless of timezone
        const dayCount = usersByMonth?.filter(user => {
          const userLocal = getLocalDateComponents(user.registered_at);
          return userLocal.year === fromDateStart.getFullYear() &&
                 userLocal.month === fromDateStart.getMonth() &&
                 userLocal.day === day;
        }).length || 0;
        
        const isInRange = day >= startDay && day <= endDay;
        
        // Only include days within the filtered date range
        if (isInRange) {
          dailyData.push({
            month: `${day}`,
            count: dayCount,
          });
        }
      }
      
      monthlySignupsData = dailyData;

      // Daily active subscriptions started - only count for days in range
      activeSubsInRange?.forEach((sub) => {
        const subLocal = getLocalDateComponents((sub as { subscription_start: string }).subscription_start);
        const day = subLocal.day;
        // Only count if it's in the same month and within the day range
        if (subLocal.year === fromDateStart.getFullYear() &&
            subLocal.month === fromDateStart.getMonth() &&
            day >= startDay && day <= endDay &&
            day >= 1 && day <= daysInMonth) {
          dailySubs[day - 1]++;
        }
      });
      // Only include subscription data for days in range
      monthlyNewSubscribersData = dailySubs
        .map((count, idx) => {
          const day = idx + 1;
          if (day >= startDay && day <= endDay) {
            return { month: `${day}`, count };
          }
          return null;
        })
        .filter((item): item is { month: string; count: number } => item !== null);
    } else if (shouldShowDaily) {
      // Date range is 28 days or less - show daily data across all days in range
      const dailyData = [];
      const dailySubsData = [];
      
      // Helper function to get local date components from a UTC date string
      const getLocalDateComponents = (utcDateString: string) => {
        const date = new Date(utcDateString);
        return {
          year: date.getFullYear(),
          month: date.getMonth(),
          day: date.getDate(),
        };
      };
      
      // Iterate through each day in the date range using normalized dates
      const currentDate = new Date(fromDateStart);
      const endDate = new Date(toDateEnd);
      
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const day = currentDate.getDate();
        
        // Count users for this specific day
        const dayCount = usersByMonth?.filter(user => {
          const userLocal = getLocalDateComponents(user.registered_at);
          return userLocal.year === year &&
                 userLocal.month === month &&
                 userLocal.day === day;
        }).length || 0;
        
        // Count subscriptions for this specific day
        const subCount = activeSubsInRange?.filter(sub => {
          const subLocal = getLocalDateComponents((sub as { subscription_start: string }).subscription_start);
          return subLocal.year === year &&
                 subLocal.month === month &&
                 subLocal.day === day;
        }).length || 0;
        
        // Format day label (e.g., "Jan 15" or "Dec 1")
        const dayLabel = `${months[month]} ${day}`;
        
        dailyData.push({
          month: dayLabel,
          count: dayCount,
        });
        
        dailySubsData.push({
          month: dayLabel,
          count: subCount,
        });
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      monthlySignupsData = dailyData;
      monthlyNewSubscribersData = dailySubsData;
    } else if (isSameYear) {
      // Same year - show monthly data for the selected range only
      const startMonth = fromDateStart.getMonth();
      const endMonth = toDateEnd.getMonth();
      const filteredMonths = [];
      
      // Helper function to get local month from UTC date string
      const getLocalMonth = (utcDateString: string) => {
        return new Date(utcDateString).getMonth();
      };
      
      // Only include months in the filtered range
      for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex++) {
        // Count users for this specific month using local date components
        const monthCount = usersByMonth?.filter(user => {
          const userMonth = getLocalMonth(user.registered_at);
          return userMonth === monthIndex;
        }).length || 0;
        
        filteredMonths.push({
          month: months[monthIndex],
          count: monthCount,
        });
      }
      
      monthlySignupsData = filteredMonths;

      // Monthly active subscriptions started - filter to selected range
      const monthlySubs: number[] = Array(12).fill(0);
      activeSubsInRange?.forEach((sub) => {
        const date = new Date((sub as { subscription_start: string }).subscription_start);
        const month = date.getMonth();
        if (month >= startMonth && month <= endMonth) {
          monthlySubs[month]++;
        }
      });
      
      // Only include months in the filtered range
      const filteredSubs = [];
      for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex++) {
        filteredSubs.push({ month: months[monthIndex], count: monthlySubs[monthIndex] });
      }
      monthlyNewSubscribersData = filteredSubs;
    } else {
      // Different years - show monthly data for the selected range
      const startMonth = fromDateStart.getMonth();
      const endMonth = toDateEnd.getMonth();
      const startYear = fromDateStart.getFullYear();
      const endYear = toDateEnd.getFullYear();
      
      const monthLabels = [];
      const monthCounts = [];
      const subsCounts: number[] = [];
      
      const currentDate = new Date(startYear, startMonth, 1);
      const endDateForLoop = new Date(endYear, endMonth + 1, 0); // Last day of end month
      endDateForLoop.setHours(23, 59, 59, 999);
      while (currentDate <= endDateForLoop) {
        const monthLabel = `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
        monthLabels.push(monthLabel);
        
        // Count users for this specific month
        const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        
        const monthCount = usersByMonth?.filter(user => {
          const userDate = new Date(user.registered_at);
          return userDate >= monthStart && userDate <= monthEnd;
        }).length || 0;
        
        monthCounts.push(monthCount);

        // Count active subs started in this month
        const subsCount = activeSubsInRange?.filter((sub) => {
          const d = new Date((sub as { subscription_start: string }).subscription_start);
          return d >= monthStart && d <= monthEnd;
        }).length || 0;
        subsCounts.push(subsCount);
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      
      monthlySignupsData = monthLabels.map((month, index) => ({
        month,
        count: monthCounts[index],
      }));
      monthlyNewSubscribersData = monthLabels.map((month, index) => ({
        month,
        count: subsCounts[index],
      }));
    }

    const result = {
      monthlySignups: monthlySignupsData,
      // Prefer this new key
      monthlyNewSubscribers: monthlyNewSubscribersData,
      // Backward compatibility (deprecated):
      monthlyActiveSubscriptions: monthlyNewSubscribersData,
      recentUsers: recentUsers?.map((user: { id: string | number; name: string; email: string; avatar_url?: string | null; registered_at: string; }) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        avatarUrl: user.avatar_url || null,
        registeredAt: user.registered_at
      })) || [],
    };
    return result;
  } catch (error) {
    console.error("Error fetching user analytics:", error);
    throw error;
  }
};

// Engagement trends (Views, Reads, Plays) over a date range
export const fetchEngagementTrends = async (dateRange: { from: Date; to: Date }) => {
  try {
    if (!dateRange?.from || !dateRange?.to) {
      throw new Error("Date range is required");
    }

    const fromDate = dateRange.from;
    const toDate = dateRange.to;
    
    // Ensure we have full day boundaries
    const fromDateStart = new Date(fromDate);
    fromDateStart.setHours(0, 0, 0, 0);
    const toDateEnd = new Date(toDate);
    toDateEnd.setHours(23, 59, 59, 999);
    
    const fromISO = fromDateStart.toISOString();
    const toISO = toDateEnd.toISOString();

    // Helper function to fetch all views with pagination
    const fetchAllViews = async (): Promise<{ viewed_at: string }[]> => {
      const allRecords: { viewed_at: string }[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("book_views")
          .select("viewed_at")
          .gte("viewed_at", fromISO)
          .lte("viewed_at", toISO)
          .order("viewed_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching views:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Helper function to fetch all reads with pagination
    const fetchAllReads = async (): Promise<{ created_at: string }[]> => {
      const allRecords: { created_at: string }[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("book_reads")
          .select("created_at")
          .gte("created_at", fromISO)
          .lte("created_at", toISO)
          .order("created_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching reads:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Helper function to fetch all plays with pagination
    const fetchAllPlays = async (): Promise<{ started_at: string }[]> => {
      const allRecords: { started_at: string }[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("book_audio_plays")
          .select("started_at")
          .gte("started_at", fromISO)
          .lte("started_at", toISO)
          .order("started_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching plays:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Fetch all records with pagination
    const [views, reads, plays] = await Promise.all([
      fetchAllViews(),
      fetchAllReads(),
      fetchAllPlays(),
    ]);

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    // Use normalized dates for comparisons
    const isSameYear = fromDateStart.getFullYear() === toDateEnd.getFullYear();
    const isSameMonth = isSameYear && fromDateStart.getMonth() === toDateEnd.getMonth();
    const isSameDay = isSameMonth && fromDateStart.getDate() === toDateEnd.getDate();

    type Point = { month: string; views: number; reads: number; plays: number };
    let series: Point[] = [];

    if (isSameDay) {
      series = [
        {
          month: `${months[fromDateStart.getMonth()]} ${fromDateStart.getDate()}, ${fromDateStart.getFullYear()}`,
          views: views.length,
          reads: reads.length,
          plays: plays.length,
        },
      ];
    } else if (isSameMonth) {
      const daysInMonth = new Date(fromDateStart.getFullYear(), fromDateStart.getMonth() + 1, 0).getDate();
      const daily = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, views: 0, reads: 0, plays: 0 }));
      views.forEach((v) => {
        const d = new Date((v as { viewed_at: string }).viewed_at);
        daily[d.getDate() - 1].views++;
      });
      reads.forEach((r) => {
        const d = new Date((r as { created_at: string }).created_at);
        daily[d.getDate() - 1].reads++;
      });
      plays.forEach((p) => {
        const d = new Date((p as { started_at: string }).started_at);
        daily[d.getDate() - 1].plays++;
      });
      series = daily.map((d) => ({ month: `${d.day}`, views: d.views, reads: d.reads, plays: d.plays }));
    } else if (isSameYear) {
      const monthly = Array.from({ length: 12 }, (_, i) => ({ idx: i, views: 0, reads: 0, plays: 0 }));
      views.forEach((v) => {
        const d = new Date((v as { viewed_at: string }).viewed_at);
        monthly[d.getMonth()].views++;
      });
      reads.forEach((r) => {
        const d = new Date((r as { created_at: string }).created_at);
        monthly[d.getMonth()].reads++;
      });
      plays.forEach((p) => {
        const d = new Date((p as { started_at: string }).started_at);
        monthly[d.getMonth()].plays++;
      });
      series = monthly.map((m) => ({ month: months[m.idx], views: m.views, reads: m.reads, plays: m.plays }));
    } else {
      // Multi-year: month-year buckets
      const bucketMap = new Map<string, { views: number; reads: number; plays: number }>();
      const addToBucket = (dateStr: string, key: "views" | "reads" | "plays") => {
        const d = new Date(dateStr);
        const label = `${months[d.getMonth()]} ${d.getFullYear()}`;
        const entry = bucketMap.get(label) || { views: 0, reads: 0, plays: 0 };
        entry[key]++;
        bucketMap.set(label, entry);
      };
      views.forEach((v) => addToBucket((v as { viewed_at: string }).viewed_at, "views"));
      reads.forEach((r) => addToBucket((r as { created_at: string }).created_at, "reads"));
      plays.forEach((p) => addToBucket((p as { started_at: string }).started_at, "plays"));

      // Sort labels chronologically
      const labels = Array.from(bucketMap.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      series = labels.map((label) => ({ month: label, ...bucketMap.get(label)! }));
    }

    return { series };
  } catch (error) {
    console.error("Error fetching engagement trends:", error);
    return { series: [] as Array<{ month: string; views: number; reads: number; plays: number }> };
  }
};

// Detailed user engagement: unique active users, top engaged users, heatmap
export const fetchUserEngagementDetails = async (
  dateRange: { from: Date; to: Date },
  topLimit: number = 10,
  offset: number = 0
): Promise<EngagementDetailsReturn> => {
  try {
    if (!dateRange?.from || !dateRange?.to) {
      throw new Error("Date range is required");
    }

    // Ensure we have full day boundaries
    const fromDateStart = new Date(dateRange.from);
    fromDateStart.setHours(0, 0, 0, 0);
    const toDateEnd = new Date(dateRange.to);
    toDateEnd.setHours(23, 59, 59, 999);
    
    const fromISO = fromDateStart.toISOString();
    const toISO = toDateEnd.toISOString();

    // Helper function to fetch all views with pagination
    const fetchAllViews = async (fromDateISO: string, toDateISO: string): Promise<Array<{ user_id: string | number | null; viewed_at: string }>> => {
      const allRecords: Array<{ user_id: string | number | null; viewed_at: string }> = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("book_views")
          .select("user_id, viewed_at")
          .gte("viewed_at", fromDateISO)
          .lte("viewed_at", toDateISO)
          .order("viewed_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching views:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Helper function to fetch all reads with pagination
    const fetchAllReads = async (fromDateISO: string, toDateISO: string): Promise<Array<{ user_id: string | number | null; created_at: string }>> => {
      const allRecords: Array<{ user_id: string | number | null; created_at: string }> = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("book_reads")
          .select("user_id, created_at")
          .gte("created_at", fromDateISO)
          .lte("created_at", toDateISO)
          .order("created_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching reads:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Helper function to fetch all plays with pagination
    const fetchAllPlays = async (fromDateISO: string, toDateISO: string): Promise<Array<{ user_id: string | number | null; started_at: string }>> => {
      const allRecords: Array<{ user_id: string | number | null; started_at: string }> = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("book_audio_plays")
          .select("user_id, started_at")
          .gte("started_at", fromDateISO)
          .lte("started_at", toDateISO)
          .order("started_at", { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          console.error("Error fetching plays:", error);
          throw error;
        }

        if (data && data.length > 0) {
          allRecords.push(...data);
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
      }

      return allRecords;
    };

    // Current period - fetch all records with pagination
    const [views, reads, plays] = await Promise.all([
      fetchAllViews(fromISO, toISO),
      fetchAllReads(fromISO, toISO),
      fetchAllPlays(fromISO, toISO),
    ]);

    // Unique users per channel and overall for current period
    const viewers = new Set<string | number>();
    views.forEach((v) => {
      if (v.user_id != null) viewers.add(v.user_id);
    });
    const readers = new Set<string | number>();
    reads.forEach((r) => {
      if (r.user_id != null) readers.add(r.user_id);
    });
    const listeners = new Set<string | number>();
    plays.forEach((p) => {
      if (p.user_id != null) listeners.add(p.user_id);
    });
    const union = new Set<string | number>([...viewers, ...readers, ...listeners]);

    // Previous period (same duration immediately before current range)
    const rangeMs = new Date(toISO).getTime() - new Date(fromISO).getTime();
    const prevFrom = new Date(new Date(fromISO).getTime() - rangeMs);
    const prevTo = new Date(new Date(fromISO).getTime() - 1);
    const prevFromStart = new Date(prevFrom);
    prevFromStart.setHours(0, 0, 0, 0);
    const prevToEnd = new Date(prevTo);
    prevToEnd.setHours(23, 59, 59, 999);
    const prevFromISO = prevFromStart.toISOString();
    const prevToISO = prevToEnd.toISOString();
    
    // Previous period - fetch all records with pagination
    const [prevViews, prevReads, prevPlays] = await Promise.all([
      fetchAllViews(prevFromISO, prevToISO),
      fetchAllReads(prevFromISO, prevToISO),
      fetchAllPlays(prevFromISO, prevToISO),
    ]);
    const prevUnion = new Set<string | number>();
    prevViews.forEach((v: { user_id: string | number | null }) => v.user_id != null && prevUnion.add(v.user_id));
    prevReads.forEach((r: { user_id: string | number | null }) => r.user_id != null && prevUnion.add(r.user_id));
    prevPlays.forEach((p: { user_id: string | number | null }) => p.user_id != null && prevUnion.add(p.user_id));
    const prevTotalUnique = prevUnion.size;
    const currTotalUnique = union.size;
    const pctChangeTotal = prevTotalUnique > 0
      ? Math.round(((currTotalUnique - prevTotalUnique) / prevTotalUnique) * 100)
      : currTotalUnique > 0 ? 100 : 0;

    // Active users trend (unique per bucket)
    const fromDate = dateRange.from;
    const toDate = dateRange.to;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const isSameYear = fromDate.getFullYear() === toDate.getFullYear();
    const isSameMonth = isSameYear && fromDate.getMonth() === toDate.getMonth();
    const isSameDay = isSameMonth && fromDate.getDate() === toDate.getDate();

    type BucketMap = Map<string, Set<string | number>>;
    const bucketMap: BucketMap = new Map();
    const addToBucket = (dateStr: string, userId: string | number | null, label: string) => {
      if (userId == null) return;
      const set = bucketMap.get(label) || new Set();
      set.add(userId);
      bucketMap.set(label, set);
    };

    const addByGranularity = (dateStr: string, userId: string | number | null) => {
      const d = new Date(dateStr);
      if (isSameDay) {
        const label = `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        addToBucket(dateStr, userId, label);
      } else if (isSameMonth) {
        const label = `${d.getDate()}`;
        addToBucket(dateStr, userId, label);
      } else if (isSameYear) {
        const label = months[d.getMonth()];
        addToBucket(dateStr, userId, label);
      } else {
        const label = `${months[d.getMonth()]} ${d.getFullYear()}`;
        addToBucket(dateStr, userId, label);
      }
    };

    views.forEach((v) => addByGranularity(v.viewed_at, v.user_id));
    reads.forEach((r) => addByGranularity(r.created_at, r.user_id));
    plays.forEach((p) => addByGranularity(p.started_at, p.user_id));

    const uniqueActiveUsersSeries = Array.from(bucketMap.entries())
      .map(([month, set]) => ({ month, count: set.size }))
      .sort((a, b) => {
        // Sort by date where possible; fallback to lexical
        const aDate = new Date(a.month);
        const bDate = new Date(b.month);
        const aTime = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
        const bTime = isNaN(bDate.getTime()) ? 0 : bDate.getTime();
        return aTime - bTime;
      });

    // Heatmap (day-of-week x hour) across all events
    const heatmap: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    const bumpHeat = (dateStr: string) => {
      const d = new Date(dateStr);
      const dow = d.getDay(); // 0 Sun - 6 Sat
      const hour = d.getHours();
      heatmap[dow][hour] += 1;
    };
    views.forEach((v) => bumpHeat(v.viewed_at));
    reads.forEach((r) => bumpHeat(r.created_at));
    plays.forEach((p) => bumpHeat(p.started_at));

    // Top engaged users by total interactions
    type Counts = { views: number; reads: number; plays: number };
    const byUser: Map<string | number, Counts> = new Map();
    const lastReadAt: Map<string | number, string> = new Map();
    const inc = (userId: string | number | null, key: keyof Counts) => {
      if (userId == null) return;
      const prev = byUser.get(userId) || { views: 0, reads: 0, plays: 0 };
      prev[key] += 1;
      byUser.set(userId, prev);
    };
    views.forEach((v) => inc(v.user_id, "views"));
    reads.forEach((r) => {
      inc(r.user_id, "reads");
      if (r.user_id != null) {
        const prev = lastReadAt.get(r.user_id);
        if (!prev || new Date(r.created_at) > new Date(prev)) {
          lastReadAt.set(r.user_id, r.created_at);
        }
      }
    });
    plays.forEach((p) => inc(p.user_id, "plays"));

    const ranked = Array.from(byUser.entries())
      .map(([userId, c]) => ({
        userId,
        views: c.views,
        reads: c.reads,
        plays: c.plays,
        total: c.views + c.reads + c.plays,
        lastReadAt: lastReadAt.get(userId) || null,
      }))
      .sort((a, b) => b.total - a.total);
    const totalRanked = ranked.length;
    const paged = ranked.slice(Math.max(0, offset), Math.max(0, offset) + topLimit);

    const topUserIds = paged.map((r) => r.userId);
    const topUserIdsNum: number[] = topUserIds.filter((id): id is number => typeof id === "number");
    let profiles: Array<{ id: string | number; name: string; email: string }> = [];
    // Subscription status lookup for top users
    type SubscriptionRow = {
      user_id: string | number;
      status: string | null;
      subscription_start: string | null;
      subscription_end: string | null;
      created_at?: string | null;
      subscription_plan?: {
        id: number;
        name: string;
      } | null;
    };
    let subs: Array<SubscriptionRow> = [];
    if (topUserIdsNum.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", topUserIdsNum as readonly number[]);
      profiles = profs || [];

      // Fetch latest subscription rows for these users with plan info
      const { data: subsData } = await supabase
        .from("user_subscriptions")
        .select(`
          user_id, 
          status, 
          subscription_start, 
          subscription_end, 
          created_at,
          subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(id, name)
        `)
        .in("user_id", topUserIdsNum as readonly number[])
        .order("created_at", { ascending: false });
      subs = subsData as Array<SubscriptionRow> | null || [];
    }

    const profileById = new Map(
      profiles.map((p) => [p.id, { name: p.name || "Unknown", email: p.email || "" }])
    );

    // Build a map of user -> most relevant subscription row
    const latestSubByUser = new Map<string | number, SubscriptionRow>();
    for (const row of subs) {
      if (!latestSubByUser.has(row.user_id)) {
        latestSubByUser.set(row.user_id, row);
      }
    }

    const nowIso = new Date().toISOString();
    const deriveSubscriptionStatus = (row?: SubscriptionRow): string => {
      if (!row) return "No Plan";
      // Determine if currently active
      const startOk = !row.subscription_start || row.subscription_start <= nowIso;
      const endOk = !row.subscription_end || row.subscription_end > nowIso;
      if ((row.status === "active" || row.status === "trialing") && startOk && endOk) {
        // Show plan name if active, fallback to "Active"
        return row.subscription_plan?.name || "Active";
      }
      if (row.status === "cancelled" || row.status === "canceled") return "Cancelled";
      if (row.subscription_end && row.subscription_end <= nowIso) return "Expired";
      return (row.status || "Inactive").charAt(0).toUpperCase() + (row.status || "inactive").slice(1);
    };

    const topUsers = paged.map((r) => ({
      id: r.userId,
      name: profileById.get(r.userId)?.name || `User ${r.userId}`,
      email: profileById.get(r.userId)?.email || "",
      views: r.views,
      reads: r.reads,
      plays: r.plays,
      total: r.total,
      lastReadAt: r.lastReadAt,
      subscriptionStatus: deriveSubscriptionStatus(latestSubByUser.get(r.userId)),
    }));

    return {
      activeUsers: {
        totalUnique: union.size,
        previousTotalUnique: prevTotalUnique,
        percentChange: pctChangeTotal,
        uniqueViewers: viewers.size,
        uniqueReaders: readers.size,
        uniqueListeners: listeners.size,
      },
      uniqueActiveUsersSeries,
      heatmap,
      topUsers,
      topUsersTotal: totalRanked,
    };
  } catch (error) {
    console.error("Error fetching user engagement details:", error);
    return {
      activeUsers: {
        totalUnique: 0,
        previousTotalUnique: 0,
        percentChange: 0,
        uniqueViewers: 0,
        uniqueReaders: 0,
        uniqueListeners: 0,
      },
      uniqueActiveUsersSeries: [] as Array<{ month: string; count: number }>,
      heatmap: Array.from({ length: 7 }, () => Array(24).fill(0)),
      topUsers: [] as TopEngagedUser[],
      topUsersTotal: 0,
    };
  }
};

